import React, { useState, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import NoImg from '../assets/images/no-image-icon-23485.png';

export interface Image {
  image_id: string;
  file_url: string;
  description?: string | null;
}

interface ImageGalleryProps {
  images: Image[];
  restaurantName: string;
  baseUrl?: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  restaurantName,
  baseUrl,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);

  // Touch state for swipe gestures
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const minSwipeDistance = 50;

  if (!images || images.length === 0) return null;

  const imageCount = images.length;

  const openModal = (index: number = 0) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const nextImage = () => setSelectedImageIndex((prev) => (prev + 1) % images.length);
  const previousImage = () => setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const nextMobileImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentMobileIndex((prev) => (prev + 1) % images.length);
  };

  const prevMobileImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentMobileIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Touch handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = 0;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextMobileImage();
    } else if (isRightSwipe) {
      prevMobileImage();
    }
  };

  const getImageUrl = (fileUrl?: string) => (fileUrl ? `${baseUrl}${fileUrl}` : NoImg);

  /* ---------- 📱 MOBILE VERSION ---------- */
  const MobileGallery = () => (
    <div
      className="relative w-full h-56 my-0 sm:hidden rounded-lg overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={getImageUrl(images[currentMobileIndex].file_url)}
        alt={images[currentMobileIndex].description || restaurantName}
        className="w-full h-full object-cover"
        loading="lazy"
        onClick={() => openModal(currentMobileIndex)}
      />

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevMobileImage(e);
            }}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white flex items-center justify-center bg-black/30 rounded-full p-1"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextMobileImage(e);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white flex items-center justify-center bg-black/30 rounded-full p-1"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </>
      )}

      <div className="absolute bottom-2 right-3 bg-black/50 font-myYekanFaNumRegular text-white text-sm px-3 py-1 rounded-full">
        {currentMobileIndex + 1} / {images.length}
      </div>
    </div>
  );

  /* ---------- 💻 DESKTOP ---------- */
  if (imageCount === 1) {
    return (
      <>
        <MobileGallery />
        <div className="hidden sm:block my-6">
          <div
            className="relative w-full h-[536px] rounded-lg overflow-hidden cursor-pointer"
            onClick={() => openModal(0)}
          >
            <img
              src={getImageUrl(images[0].file_url)}
              alt={images[0].description || restaurantName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        {isModalOpen && (
          <ImageModal
            images={images}
            currentIndex={selectedImageIndex}
            onClose={closeModal}
            onNext={nextImage}
            onPrevious={previousImage}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
          />
        )}
      </>
    );
  }

  if (imageCount === 2) {
    return (
      <>
        <MobileGallery />
        <div className="hidden sm:flex gap-2 my-6">
          {images.map((img, index) => (
            <div
              key={img.image_id}
              className="relative flex-1 h-[536px] rounded-lg overflow-hidden cursor-pointer"
              onClick={() => openModal(index)}
            >
              <img
                src={getImageUrl(img.file_url)}
                alt={img.description || restaurantName}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
        {isModalOpen && (
          <ImageModal
            images={images}
            currentIndex={selectedImageIndex}
            onClose={closeModal}
            onNext={nextImage}
            onPrevious={previousImage}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
          />
        )}
      </>
    );
  }

  if (imageCount === 3) {
    return (
      <>
        <MobileGallery />
        <div className="hidden sm:flex gap-2 my-6">
          <div className="flex flex-col gap-2 flex-1 h-[536px]">
            {images.slice(0, 2).map((img, index) => (
              <div
                key={img.image_id}
                className="relative flex-1 rounded-lg overflow-hidden cursor-pointer"
                onClick={() => openModal(index)}
              >
                <img
                  src={getImageUrl(img.file_url)}
                  alt={img.description || restaurantName}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <div
            className="relative flex-1 h-[536px] rounded-lg overflow-hidden cursor-pointer"
            onClick={() => openModal(2)}
          >
            <img
              src={getImageUrl(images[2].file_url)}
              alt={images[2].description || restaurantName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        {isModalOpen && (
          <ImageModal
            images={images}
            currentIndex={selectedImageIndex}
            onClose={closeModal}
            onNext={nextImage}
            onPrevious={previousImage}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
          />
        )}
      </>
    );
  }

  const displayedImages = images.slice(0, 4);

  return (
    <>
      <MobileGallery />
      <div className="hidden sm:flex gap-2 my-6">
        {/* BIG IMAGE (index 0) */}
        {displayedImages[0] && (
          <div
            className="relative w-full h-[536px] rounded-lg overflow-hidden cursor-pointer"
            onClick={() => openModal(0)}
          >
            <img
              src={getImageUrl(displayedImages[0].file_url)}
              alt={displayedImages[0].description || restaurantName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        {/* RIGHT SIDE SMALL IMAGES */}
        <div className="flex flex-col gap-2 h-[536px] w-full">
          {/* Top right (index 1) */}
          {displayedImages[1] && (
            <div
              className="relative w-full h-[256px] rounded-lg overflow-hidden cursor-pointer"
              onClick={() => openModal(1)}
            >
              <img
                src={getImageUrl(displayedImages[1].file_url)}
                alt={displayedImages[1].description || restaurantName}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          {/* Bottom right (index 2 & 3) */}
          <div className="flex gap-2 flex-1 w-full">
            {displayedImages.slice(2, 4).map((img, idx) => {
              const realIndex = idx + 2;
              return (
                <div
                  key={img.image_id}
                  className="relative flex-1 rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => openModal(realIndex)}
                >
                  <img
                    src={getImageUrl(img.file_url)}
                    alt={img.description || restaurantName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Show overlay if more than 4 images */}
                  {realIndex === 3 && images.length > 4 && (
                    <div className="absolute inset-0 flex font-myYekanFaNumRegular text-xl items-center justify-center bg-black/50 text-white">
                      +{images.length - 4}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <ImageModal
          images={images}
          currentIndex={selectedImageIndex}
          onClose={closeModal}
          onNext={nextImage}
          onPrevious={previousImage}
          restaurantName={restaurantName}
          baseUrl={baseUrl}
        />
      )}
    </>
  );
};

/* ---------- 🖼️ MODAL COMPONENT ---------- */
interface ImageModalProps {
  images: Image[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  restaurantName: string;
  baseUrl?: string;
}

const ImageModal: React.FC<ImageModalProps> = ({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrevious,
  restaurantName,
  baseUrl,
}) => {
  const currentImage = images[currentIndex];
  const getImageUrl = (fileUrl?: string) => (fileUrl ? `${baseUrl}${fileUrl}` : NoImg);

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-10"
        onClick={onClose}
      >
        ×
      </button>
      {images.length > 1 && (
        <button
          className="absolute left-4 text-white text-4xl hover:text-gray-300 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
        >
          ›
        </button>
      )}
      <div
        className="max-w-6xl max-h-[90vh] mx-auto px-16"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getImageUrl(currentImage.file_url)}
          alt={currentImage.description || restaurantName}
          className="max-w-full max-h-[90vh] object-contain"
        />
        <div className="text-white text-center mt-4 font-myYekanFaNumRegular">
          {currentIndex + 1} / {images.length}
        </div>
        {currentImage.description && (
          <p className="text-white text-center mt-2">{currentImage.description}</p>
        )}
      </div>
      {images.length > 1 && (
        <button
          className="absolute right-4 text-white text-4xl hover:text-gray-300 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
        >
          ‹
        </button>
      )}
    </div>
  );
};

export default ImageGallery;